import type { MfrrBid, MfrrBidResponse } from 'api/mfrr/mfrr.types'
import Datetime from 'utils/datetime/datetime'

type ComparableBid = {
  direction: string
  production_unit_id?: number | null
  start_time: ISODateTime
  volume: number
  price: number
}

type MfrrBidActionAvailabilityParams = {
  isBidsLoading: boolean
  isLocked: boolean
  hasDraftBids: boolean
  eligibilityCanSubmit: boolean
  hasSystemId: boolean
  payloadCount: number
  hasLocalChanges: boolean
  isSaving: boolean
  isPublishing: boolean
}

type MfrrBidActionAvailability = {
  canSave: boolean
  canLock: boolean
}

type MfrrEmptyTableAvailabilityParams = {
  isBidsLoading: boolean
  isLocked: boolean
  hasUnits: boolean
  hasEditableValues: boolean
}

const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000
const ONE_SECOND_IN_MS = 1000

export function getMfrrDeliveryWindowEnd(dateStartTime: ISODateTime): ISODateTime {
  const lastInstant = new Date(new Date(dateStartTime).getTime() + MILLISECONDS_PER_DAY - ONE_SECOND_IN_MS)
  return Datetime.dateToISODateTime(lastInstant)
}

function getBidSlotKey(
  bid:
    | Pick<MfrrBid, 'direction' | 'production_unit_id' | 'start_time'>
    | Pick<MfrrBidResponse, 'direction' | 'production_unit_id' | 'start_time'>
): string {
  return `${bid.direction}|${bid.production_unit_id ?? ''}|${new Date(bid.start_time).getTime()}`
}

export function mapMfrrBidIdsForUpdate(currentBids: MfrrBid[], savedBids: MfrrBidResponse[]): MfrrBid[] {
  const draftIdBySlot = new Map<string, number>()
  const publishedBySlot = new Map<string, MfrrBidResponse>()

  for (const bid of savedBids) {
    const slotKey = getBidSlotKey(bid)

    if (bid.status === 'published') {
      publishedBySlot.set(slotKey, bid)
    } else {
      draftIdBySlot.set(slotKey, bid.id)
    }
  }

  return currentBids.flatMap((bid): MfrrBid[] => {
    const slotKey = getBidSlotKey(bid)
    const draftId = draftIdBySlot.get(slotKey)

    if (draftId !== undefined) {
      return [{ ...bid, id: draftId }]
    }

    const isEmptyBid = bid.volume === 0 && bid.price === 0

    if (isEmptyBid) {
      return []
    }

    const publishedBid = publishedBySlot.get(slotKey)

    if (publishedBid !== undefined && bidValuesMatch(publishedBid, bid)) {
      return []
    }

    return [{ ...bid, id: undefined }]
  })
}

export const mapMfrrCmBidIdsForUpdate = mapMfrrBidIdsForUpdate

function isNewerMfrrBid(candidate: MfrrBidResponse, current: MfrrBidResponse): boolean {
  const candidateTime = new Date(candidate.updated_at).getTime()
  const currentTime = new Date(current.updated_at).getTime()

  if (candidateTime !== currentTime) {
    return candidateTime > currentTime
  }

  return candidate.id > current.id
}

export function getLatestMfrrBidsPerSlot(bids: MfrrBidResponse[]): MfrrBidResponse[] {
  const latestBySlot = new Map<string, MfrrBidResponse>()

  for (const bid of bids) {
    const key = getBidSlotKey(bid)
    const existing = latestBySlot.get(key)

    if (existing === undefined || isNewerMfrrBid(bid, existing)) {
      latestBySlot.set(key, bid)
    }
  }

  return Array.from(latestBySlot.values())
}

export function getEffectiveMfrrSavedBids(
  savedBids: MfrrBidResponse[],
  publishedBids: MfrrBidResponse[],
  isSlotClosed: (startTime: ISODateTime) => boolean
): MfrrBidResponse[] {
  const openSavedBids = getLatestMfrrBidsPerSlot(savedBids).filter((bid) => !isSlotClosed(bid.start_time))
  const closedPublishedBids = getLatestMfrrBidsPerSlot(publishedBids).filter((bid) => isSlotClosed(bid.start_time))

  return [...openSavedBids, ...closedPublishedBids]
}

function roundForComparison(value: number): number {
  return Math.round(value * 100) / 100
}

function bidValuesMatch(
  saved: Pick<MfrrBidResponse, 'volume' | 'price'>,
  current: Pick<MfrrBid, 'volume' | 'price'>
): boolean {
  return (
    roundForComparison(saved.volume) === roundForComparison(current.volume) &&
    roundForComparison(saved.price) === roundForComparison(current.price)
  )
}

export function normalizeMfrrBidsForComparison(bids: ComparableBid[]): string {
  return bids
    .filter((bid) => bid.volume !== 0 || bid.price !== 0)
    .map((bid) =>
      [
        bid.direction,
        bid.production_unit_id ?? '',
        new Date(bid.start_time).getTime(),
        roundForComparison(bid.volume),
        roundForComparison(bid.price),
      ].join('|')
    )
    .sort()
    .join(';')
}

export const normalizeMfrrCmBidsForComparison = normalizeMfrrBidsForComparison

export function getMfrrBidActionAvailability({
  isBidsLoading,
  isLocked,
  hasDraftBids,
  eligibilityCanSubmit,
  hasSystemId,
  payloadCount,
  hasLocalChanges,
  isSaving,
  isPublishing,
}: MfrrBidActionAvailabilityParams): MfrrBidActionAvailability {
  const canSave =
    !isBidsLoading &&
    !isLocked &&
    eligibilityCanSubmit &&
    hasSystemId &&
    payloadCount > 0 &&
    hasLocalChanges &&
    !isSaving

  const canLock = !isBidsLoading && hasDraftBids && !hasLocalChanges && !isPublishing && !isSaving

  return { canSave, canLock }
}

export function getCanEmptyMfrrTable({
  isBidsLoading,
  isLocked,
  hasUnits,
  hasEditableValues,
}: MfrrEmptyTableAvailabilityParams): boolean {
  return !isBidsLoading && !isLocked && hasUnits && hasEditableValues
}

export type MfrrBidStatusInfo =
  | {
      name: string
      time: string
      isLocked: boolean
    }
  | undefined

function getMfrrBidStatusTimestamp(bid: MfrrBidResponse): ISODateTime | undefined {
  return bid.status === 'published' ? (bid.published_at ?? undefined) : bid.updated_at
}

export function getMfrrBidStatusInfo(savedBids: MfrrBidResponse[], isLocked: boolean): MfrrBidStatusInfo {
  const mostRecentBid = savedBids.reduce<{ bid: MfrrBidResponse; timestamp: ISODateTime } | undefined>(
    (latest, currentBid) => {
      const currentTimestamp = getMfrrBidStatusTimestamp(currentBid)

      if (!currentTimestamp) {
        return latest
      }

      const currentTime = Date.parse(currentTimestamp)

      if (!Number.isFinite(currentTime)) {
        return latest
      }

      if (!latest) {
        return { bid: currentBid, timestamp: currentTimestamp }
      }

      const latestTime = Date.parse(latest.timestamp)

      if (!Number.isFinite(latestTime) || currentTime > latestTime) {
        return { bid: currentBid, timestamp: currentTimestamp }
      }

      return latest
    },
    undefined
  )

  if (!mostRecentBid?.bid.updated_by_name) return undefined

  const formattedDateTime = Datetime.toLocalTime(mostRecentBid.timestamp, 'longDay')
  return { name: mostRecentBid.bid.updated_by_name, time: formattedDateTime, isLocked }
}
